import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Fire, Trophy, ArrowRight } from "@phosphor-icons/react";
import { formatWorkDate } from "@/lib/time";

const dayWord = (n) => (n === 1 ? "day" : "days");

/** Current + longest work streak for the signed-in employee. */
export const StreakCard = ({ compact = false }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        api.get("/streaks/me")
            .then(({ data: d }) => { if (!cancelled) setData(d); })
            .catch(() => { if (!cancelled) setData(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    if (loading) return <Skeleton className="h-32 rounded-md" data-testid="streak-card-loading" />;

    return (
        <Card className="border-[#E2E8F0] overflow-hidden" data-testid="streak-card">
            <CardContent className="p-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-[#E2E8F0]">
                    <div className="p-5 sm:p-6">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">
                            Current work streak
                        </div>
                        <div className="flex items-center gap-2.5">
                            <Fire size={30} weight="fill" className="text-[#E6002C]" />
                            <span className="text-4xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}
                                data-testid="streak-current-value">
                                {data?.current_streak ?? 0}
                            </span>
                            <span className="text-sm text-slate-500 pb-1">{dayWord(data?.current_streak)}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-2">
                            {data?.last_worked_date
                                ? `Last worked ${formatWorkDate(data.last_worked_date)}`
                                : "Clock in to start your streak"}
                        </div>
                    </div>
                    <div className="p-5 sm:p-6">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">
                            Longest work streak
                        </div>
                        <div className="flex items-center gap-2.5">
                            <Trophy size={28} weight="fill" className="text-amber-500" />
                            <span className="text-4xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}
                                data-testid="streak-longest-value">
                                {data?.longest_streak ?? 0}
                            </span>
                            <span className="text-sm text-slate-500 pb-1">{dayWord(data?.longest_streak)}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-2" data-testid="streak-rank">
                            {data?.rank ? `Rank #${data.rank} of ${data.ranked_total}` : "Not ranked yet"}
                        </div>
                    </div>
                </div>
                {!compact && (
                    <div className="px-5 sm:px-6 py-3 bg-slate-50 border-t border-[#E2E8F0] text-xs text-slate-500 flex items-center justify-between gap-3">
                        <span>Approved time off and no-workload days never break your streak.</span>
                        <Link to="/leaderboard" className="text-[#0066D9] font-medium whitespace-nowrap flex items-center gap-1"
                            data-testid="streak-card-leaderboard-link">
                            Leaderboard <ArrowRight size={13} />
                        </Link>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

/** Compact Top-10 preview for the dashboards. */
export const TopStreaksPreview = ({ limit = 5 }) => {
    const [rows, setRows] = useState([]);
    const [me, setMe] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        api.get("/streaks/leaderboard?limit=10")
            .then(({ data: d }) => {
                if (cancelled) return;
                setRows(Array.isArray(d?.top) ? d.top : []);
                setMe(d?.me || null);
            })
            .catch(() => { if (!cancelled) setRows([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    return (
        <Card className="border-[#E2E8F0]" data-testid="top-streaks-preview">
            <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                            Recognition
                        </div>
                        <h3 className="text-lg font-semibold text-[#1F2A44]" style={{ fontFamily: "Outfit" }}>
                            Top work streaks
                        </h3>
                    </div>
                    <Trophy size={22} weight="duotone" className="text-amber-500" />
                </div>
                {loading ? (
                    <Skeleton className="h-24 rounded-md" />
                ) : rows.length === 0 ? (
                    <div className="text-sm text-slate-500 py-4">No streaks recorded yet.</div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {rows.slice(0, limit).map((r) => {
                            const mine = me && r.technician_id === me.technician_id;
                            return (
                                <div key={r.technician_id}
                                    className={`flex items-center gap-3 py-2.5 px-2 -mx-2 rounded ${mine ? "bg-blue-50" : ""}`}
                                    data-testid={`preview-streak-row-${r.rank}`}>
                                    <span className="w-6 text-xs font-mono text-slate-400">#{r.rank}</span>
                                    <span className={`flex-1 truncate text-sm ${mine ? "font-semibold text-[#003366]" : "text-slate-700"}`}>
                                        {r.full_name}{mine ? " (you)" : ""}
                                    </span>
                                    <span className="text-sm font-semibold text-[#003366] whitespace-nowrap">
                                        {r.current_streak} {dayWord(r.current_streak)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
                <Link to="/leaderboard" className="mt-4 inline-flex items-center gap-1 text-sm text-[#0066D9] font-medium"
                    data-testid="preview-view-leaderboard-link">
                    View full leaderboard <ArrowRight size={14} />
                </Link>
            </CardContent>
        </Card>
    );
};
